import React from 'react';

import Modal from 'react-bootstrap/Modal';
import Button from 'react-bootstrap/Button';
import Form from 'react-bootstrap/Form';
import Spinner from 'react-bootstrap/Spinner';
import * as FileSaver from 'file-saver';
import { PDFDocument, degrees } from 'pdf-lib';
import { toast } from 'react-toastify';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import useComponentState from '../../utils/useComponentState';
import { PRINTER_TASKS, connectPrinterForTask } from '../../utils/printerSettings';
import { getCarrierBook, getGiftBoxPdf, getQrPdf } from '../../api/Vouchers';
import {
  generateAndSavePdf,
  formatAmount,
  dedupeLabels,
  escapeZplHex,
} from '../../screens/ViewVouchers/ViewVouchersUtils';
import {
  QR_METHODS,
  LABEL_FIELDS,
  MAX_LABEL_FIELDS,
  DELIVERY_BOOK_VARIANTS,
  DELIVERY_INITIAL_STATE,
} from '../../constants/viewVouchers';

import {
  faBook,
  faGift,
  faQrcode,
  faTags,
  faBoxOpen,
  faPrint,
  faCheck,
  faPlus,
} from '@fortawesome/free-solid-svg-icons';

import styles from './PrintOptionsModal.module.scss';


const valueForLabelField = (
  key,
  { ownerName, sender, amount, id, purchaseId, invoiceNumber }
) => {
  switch (key) {
    case 'receiverName':
      return ownerName;
    case 'senderName':
      return sender;
    case 'amount':
      return `LKR ${formatAmount(amount)}`;
    case 'purchaseId':
      return purchaseId;
    case 'voucherId':
      return id;
    case 'voucherNumber':
      return invoiceNumber;
    default:
      return '';
  }
};

const computeLabelFieldValues = (fields, entry) => {
  let topField = '';
  let bottomField = null;

  if (fields.length > 1) {
    if (fields.includes('voucherId')) {
      const otherKey = fields.find((key) => key !== 'voucherId');
      topField = valueForLabelField(otherKey, entry) || '';
      bottomField = entry.id;
    } else {
      topField = valueForLabelField(fields[0], entry) || '';
      bottomField = valueForLabelField(fields[1], entry) || null;
    }
  } else {
    topField = valueForLabelField(fields[0], entry) || '';
  }

  return { topField, bottomField };
};

const rotatePdfToPortrait = async (base64Pdf) => {
  const pdfBytes = Buffer.from(base64Pdf, 'base64');
  const pdfDoc = await PDFDocument.load(pdfBytes);

  pdfDoc.getPages().forEach((page) => {
    const { width, height } = page.getSize();
    if (width > height) {
      page.setRotation(degrees(90));
    }
  });

  return pdfDoc.save();
};

const buildLabelZpl = (labelsToPrint, fields, quantity = 1) => {
  let zpl = '';

  dedupeLabels(labelsToPrint).forEach((entry) => {
    const { topField, bottomField } = computeLabelFieldValues(fields, entry);

    const fontsize = topField.length <= 20 ? 45 : topField.length <= 30 ? 40 : 25;
    const topFieldYOffset = fields.length > 1 ? 40 : 50;
    const bottomFieldYOffset = fields.length > 1 ? 120 : 130;
    const footerYOffset = 180;

    const labelZpl = `^XA
^CI28
^MD22
^PW799
^LL300
^FO0,${topFieldYOffset}
^FB799,1,0,C,0
^CF0,${fontsize},${fontsize}
^FH^FD${escapeZplHex(topField)}^FS

${
      bottomField
        ? `^FO0,${bottomFieldYOffset}
^FB799,1,0,C,0
^A0N,25,25
^FH^FD${escapeZplHex(bottomField)}^FS`
        : ''
    }

^FO0,${footerYOffset}
^FB799,1,0,C,0
^A0N,18,18
^FH^FDOne voucher, 300+ merchants | thyaga.lk^FS
^XZ`;

    zpl += labelZpl.repeat(quantity);
  });

  return zpl;
};

const DeliveryPrintOptionsModal = (props) => {
  const { show, handleClose, selectedOrders = [], labelsToPrint = [], token } = props;

  const [
    {
      bookEnabled,
      bookVariant,
      giftBoxEnabled,
      giftBoxVariant,
      qrEnabled,
      qrMethod,
      labelsEnabled,
      labelFields,
      labelsQuantity,
      packageEnabled,
      packageFields,
      packageQuantity,
      printing,
    },
    setState,
  ] = useComponentState(DELIVERY_INITIAL_STATE);

  const voucherIds = selectedOrders.map((order) => order.voucherId);

  const handleModalClose = () => {
    setState(DELIVERY_INITIAL_STATE);
    handleClose();
  };

  const toggleLabelField = (fieldsKey, key) => {
    setState((prev) => {
      const current = prev[fieldsKey];
      let updated;
      if (current.includes(key)) {
        updated = current.filter((item) => item !== key);
      } else if (current.length >= MAX_LABEL_FIELDS) {
        return prev;
      } else {
        updated = [...current, key];
      }
      return { ...prev, [fieldsKey]: updated };
    });
  };

  const handlePackageQuantityChange = (value) => {
    if (value === '' || /^[0-9]{1,3}$/.test(value)) {
      setState({ packageQuantity: value });
    }
  };

  const handlePackageQuantityBlur = () => {
    const num = Number(packageQuantity);
    const clamped = !num || num < 1 ? 1 : num > 999 ? 999 : num;
    setState({ packageQuantity: clamped });
  };

  const handleLabelsQuantityChange = (value) => {
    if (value === '' || /^[0-9]{1,3}$/.test(value)) {
      setState({ labelsQuantity: value });
    }
  };

  const handleLabelsQuantityBlur = () => {
    const num = Number(labelsQuantity);
    const clamped = !num || num < 1 ? 1 : num > 999 ? 999 : num;
    setState({ labelsQuantity: clamped });
  };

  const printBook = async () => {
    const variant = DELIVERY_BOOK_VARIANTS.find((item) => item.value === bookVariant);
    const request = voucherIds.map((id) => ({
      voucherId: id,
      amount: true,
      to: variant.to,
      from: variant.from,
      body: variant.body,
    }));
    const carrierBookResponse = await getCarrierBook(request, token);
    const rotatedPdfBytes = await rotatePdfToPortrait(carrierBookResponse);
    const blob = new Blob([rotatedPdfBytes], { type: 'application/pdf' });
    FileSaver.saveAs(blob, `Delivery_Book_${variant.value}`);
  };

  const printGiftBox = async () => {
    const variant = DELIVERY_BOOK_VARIANTS.find((item) => item.value === giftBoxVariant);
    const request = voucherIds.map((id) => ({
      voucherId: id,
      amount: true,
      to: variant.to,
      from: variant.from,
      body: variant.body,
    }));
    const giftBoxResponse = await getGiftBoxPdf(request, token);
    const pdfBytes = Buffer.from(giftBoxResponse, 'base64');
    const blob = new Blob([pdfBytes], { type: 'application/pdf' });
    FileSaver.saveAs(blob, `Delivery_Gift_Box_${variant.value}`);
  };

  const printQrZebra = async () => {
    const SHEET_SIZE = 3;
    let index = 0;
    let device = null;

    try {
      device = await connectPrinterForTask(PRINTER_TASKS.deliveryQr.key, token);
      const encoder = new TextEncoder();

      while (index < voucherIds.length) {
        let zpl = '^XA\n^MD22\n^PW799\n^LL799\n';

        for (let pos = 0; pos < SHEET_SIZE && index < voucherIds.length; pos++) {
          const id = voucherIds[index];
          const positionX = pos === 0 ? 38 : pos === 1 ? 302 : 566;
          const textX = pos === 0 ? 70 : pos === 1 ? 334 : 600;

          zpl += `^FO${positionX},6^BQN,2,6^FDQA,https://thyaga.lk/checkbalance/${id}^FS\n`;
          zpl += `^FO${textX},220^A0N,28,28^FD${id}^FS\n`;

          index++;
        }

        zpl += '^XZ\n';
        await device.transferOut(1, encoder.encode(zpl));
        await new Promise((resolve) => setTimeout(resolve, 800));
      }
    } finally {
      if (device) {
        try {
          await device.releaseInterface(0);
        } catch (e) {}
        try {
          await device.close();
        } catch (e) {}
      }
    }
  };

  const downloadQrPdf = async () => {
    const request = { voucherIds };
    const rawPdfResponse = await getQrPdf(request, token);
    generateAndSavePdf(rawPdfResponse, 'QR_Code');
  };

  const printLabelZebra = async (fields, taskKey, quantity = 1) => {
    const zpl = buildLabelZpl(labelsToPrint, fields, quantity);
    const device = await connectPrinterForTask(taskKey, token);
    const encoder = new TextEncoder();
    await device.transferOut(1, encoder.encode(zpl));
  };

  const handlePrintSelected = async () => {
    setState({ printing: true });
    const failedSections = [];

    if (bookEnabled) {
      try {
        await printBook();
      } catch (error) {
        console.error('Print options: failed to generate book', error);
        failedSections.push('Print book');
      }
    }

    if (giftBoxEnabled) {
      try {
        await printGiftBox();
      } catch (error) {
        console.error('Print options: failed to generate gift box', error);
        failedSections.push('Print gift box');
      }
    }

    if (qrEnabled) {
      try {
        if (qrMethod === 'zebra') {
          await printQrZebra();
        } else {
          await downloadQrPdf();
        }
      } catch (error) {
        console.error('Print options: failed to process QR codes', error);
        failedSections.push('QR codes');
      }
    }

    if (labelsEnabled && labelFields.length > 0) {
      try {
        await printLabelZebra(
          labelFields,
          PRINTER_TASKS.deliveryLabels.key,
          Number(labelsQuantity) || 1
        );
      } catch (error) {
        console.error('Print options: failed to print name labels', error);
        failedSections.push('Name labels');
      }
    }

    if (packageEnabled && packageFields.length > 0) {
      try {
        await printLabelZebra(
          packageFields,
          PRINTER_TASKS.deliveryPackage.key,
          Number(packageQuantity) || 1
        );
      } catch (error) {
        console.error('Print options: failed to print package label', error);
        failedSections.push('Package label');
      }
    }

    setState({ printing: false });

    if (failedSections.length === 0) {
      toast.success('Selected outputs processed successfully');
      handleModalClose();
    } else {
      toast.error(`Failed to process: ${failedSections.join(', ')}`);
    }
  };

  const outputsSelectedCount = [
    bookEnabled,
    giftBoxEnabled,
    qrEnabled,
    labelsEnabled,
    packageEnabled,
  ].filter(Boolean).length;

  const labelsInvalid = labelsEnabled && labelFields.length === 0;
  const packageInvalid = packageEnabled && packageFields.length === 0;
  const canPrint = outputsSelectedCount > 0 && !labelsInvalid && !packageInvalid && !printing;

  return (
    <>
      {show && <div className={styles.customBackdrop} onClick={handleModalClose} />}
      <Modal
        show={show}
        onHide={handleModalClose}
        backdrop={false}
        centered
        size='lg'
        className={styles.modal}
      >
      <Modal.Header closeButton>
        <Modal.Title className={styles.titleRow}>
          <span>Print options</span>
          <span className={styles.subtitle}>
            {selectedOrders.length} order{selectedOrders.length !== 1 ? 's' : ''} selected
          </span>
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div className={styles.section}>
          <div
            className={styles.sectionHeader}
            style={{ cursor: 'pointer' }}
            onClick={() => setState({ bookEnabled: !bookEnabled })}
          >
            <Form.Check
              type='checkbox'
              checked={bookEnabled}
              onChange={(e) => setState({ bookEnabled: e.target.checked })}
              onClick={(e) => e.stopPropagation()}
            />
            <FontAwesomeIcon icon={faBook} className={styles.sectionIcon} />
            <div className={styles.sectionText}>
              <p className={styles.sectionTitle}>Print book</p>
              <p className={styles.sectionSubtitle}>Choose what shows on the book</p>
            </div>
            <Form.Control
              as='select'
              className={styles.sectionSelect}
              disabled={!bookEnabled}
              value={bookVariant}
              onClick={(e) => e.stopPropagation()}
              onChange={(e) => setState({ bookVariant: e.target.value })}
            >
              {DELIVERY_BOOK_VARIANTS.map(({ value, label }) => (
                <option key={value} value={value}>
                  {label}
                </option>
              ))}
            </Form.Control>
          </div>
        </div>

        <div className={styles.section}>
          <div
            className={styles.sectionHeader}
            style={{ cursor: 'pointer' }}
            onClick={() => setState({ giftBoxEnabled: !giftBoxEnabled })}
          >
            <Form.Check
              type='checkbox'
              checked={giftBoxEnabled}
              onChange={(e) => setState({ giftBoxEnabled: e.target.checked })}
              onClick={(e) => e.stopPropagation()}
            />
            <FontAwesomeIcon icon={faGift} className={styles.sectionIcon} />
            <div className={styles.sectionText}>
              <p className={styles.sectionTitle}>Print gift box</p>
              <p className={styles.sectionSubtitle}>Choose what shows on the gift box</p>
            </div>
            <Form.Control
              as='select'
              className={styles.sectionSelect}
              disabled={!giftBoxEnabled}
              value={giftBoxVariant}
              onClick={(e) => e.stopPropagation()}
              onChange={(e) => setState({ giftBoxVariant: e.target.value })}
            >
              {DELIVERY_BOOK_VARIANTS.map(({ value, label }) => (
                <option key={value} value={value}>
                  {label}
                </option>
              ))}
            </Form.Control>
          </div>
        </div>

        <div className={styles.section}>
          <div
            className={styles.sectionHeader}
            style={{ cursor: 'pointer' }}
            onClick={() => setState({ qrEnabled: !qrEnabled })}
          >
            <Form.Check
              type='checkbox'
              checked={qrEnabled}
              onChange={(e) => setState({ qrEnabled: e.target.checked })}
              onClick={(e) => e.stopPropagation()}
            />
            <FontAwesomeIcon icon={faQrcode} className={styles.sectionIcon} />
            <div className={styles.sectionText}>
              <p className={styles.sectionTitle}>QR codes</p>
              <p className={styles.sectionSubtitle}>Pick output method</p>
            </div>
            <Form.Control
              as='select'
              className={styles.sectionSelect}
              disabled={!qrEnabled}
              value={qrMethod}
              onClick={(e) => e.stopPropagation()}
              onChange={(e) => setState({ qrMethod: e.target.value })}
            >
              {QR_METHODS.map(({ value, label }) => (
                <option key={value} value={value}>
                  {label}
                </option>
              ))}
            </Form.Control>
          </div>
        </div>

        <div className={`${styles.section} ${styles.envelopeSection}`}>
          <div
            className={`${styles.sectionHeader} ${styles.envelopeHeader}`}
            style={{ cursor: 'pointer' }}
            onClick={() => setState({ labelsEnabled: !labelsEnabled })}
          >
            <Form.Check
              type='checkbox'
              checked={labelsEnabled}
              onChange={(e) => setState({ labelsEnabled: e.target.checked })}
              onClick={(e) => e.stopPropagation()}
            />
            <FontAwesomeIcon icon={faTags} className={styles.sectionIcon} />
            <div className={styles.sectionText}>
              <p className={styles.sectionTitle}>Print name labels</p>
            </div>
            <div className={styles.qtyGroup}>
              <span className={styles.qtyLabel}>How many copies</span>
              <Form.Control
                type='text'
                inputMode='numeric'
                maxLength={3}
                className={styles.qtyInput}
                disabled={!labelsEnabled}
                value={labelsQuantity}
                onClick={(e) => e.stopPropagation()}
                onChange={(e) => handleLabelsQuantityChange(e.target.value)}
                onBlur={handleLabelsQuantityBlur}
              />
            </div>
          </div>
          <div className={styles.chipRow}>
            {LABEL_FIELDS.map(({ key, label }) => {
              const selected = labelFields.includes(key);
              const disabled =
                !labelsEnabled || (!selected && labelFields.length >= MAX_LABEL_FIELDS);
              return (
                <button
                  type='button'
                  key={key}
                  className={`${styles.chip} ${styles.envelopeChip} ${
                    selected ? styles.chipSelected : ''
                  }`}
                  disabled={disabled}
                  onClick={() => toggleLabelField('labelFields', key)}
                >
                  <FontAwesomeIcon icon={selected ? faCheck : faPlus} className={styles.chipIcon} />
                  {label}
                </button>
              );
            })}
          </div>
          {labelsInvalid && <small className='text-danger'>Choose at least one field</small>}
        </div>

        <div className={`${styles.section} ${styles.packageSection}`}>
          <div
            className={`${styles.sectionHeader} ${styles.packageHeader}`}
            style={{ cursor: 'pointer' }}
            onClick={() => setState({ packageEnabled: !packageEnabled })}
          >
            <Form.Check
              type='checkbox'
              checked={packageEnabled}
              onChange={(e) => setState({ packageEnabled: e.target.checked })}
              onClick={(e) => e.stopPropagation()}
            />
            <FontAwesomeIcon icon={faBoxOpen} className={styles.sectionIcon} />
            <div className={styles.sectionText}>
              <p className={styles.sectionTitle}>Print label on package</p>
            </div>
            <div className={styles.qtyGroup}>
              <span className={styles.qtyLabel}>How many copies</span>
              <Form.Control
                type='text'
                inputMode='numeric'
                maxLength={3}
                className={styles.qtyInput}
                disabled={!packageEnabled}
                value={packageQuantity}
                onClick={(e) => e.stopPropagation()}
                onChange={(e) => handlePackageQuantityChange(e.target.value)}
                onBlur={handlePackageQuantityBlur}
              />
            </div>
          </div>
          <div className={styles.chipRow}>
            {LABEL_FIELDS.map(({ key, label }) => {
              const selected = packageFields.includes(key);
              const disabled =
                !packageEnabled || (!selected && packageFields.length >= MAX_LABEL_FIELDS);
              return (
                <button
                  type='button'
                  key={key}
                  className={`${styles.chip} ${styles.packageChip} ${
                    selected ? styles.chipSelected : ''
                  }`}
                  disabled={disabled}
                  onClick={() => toggleLabelField('packageFields', key)}
                >
                  <FontAwesomeIcon icon={selected ? faCheck : faPlus} className={styles.chipIcon} />
                  {label}
                </button>
              );
            })}
          </div>
          {packageInvalid && <small className='text-danger'>Choose at least one field</small>}
        </div>
      </Modal.Body>
      <Modal.Footer className={styles.footer}>
        <span className={styles.outputsSelected}>
          {outputsSelectedCount} output{outputsSelectedCount !== 1 ? 's' : ''} selected
        </span>
        <div style={{ display: 'flex', gap: '10px' }}>
          <Button variant='secondary' onClick={handleModalClose}>
            Cancel
          </Button>
          <Button variant='primary' onClick={handlePrintSelected} disabled={!canPrint}>
            {printing ? (
              <Spinner size='sm' animation='border' />
            ) : (
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: '8px' }}>
                <FontAwesomeIcon icon={faPrint} />
                Print selected
              </span>
            )}
          </Button>
        </div>
      </Modal.Footer>
      </Modal>
    </>
  );
};

export default DeliveryPrintOptionsModal;
